import { useState } from 'react'
import {
  DATE_FILTER_UNITS,
  type DateFilterDirection,
  type DateFilterUnit,
  formatDateFilterValue,
  parseDateFilterValue,
} from 'app/lib/workflows/dateFilterValue'
import { DateInput } from './DateInput'
import { ChoiceSelect, type Choice, TextField } from './fields'

/** How the comparison value is being written. Everything except `custom` maps to exactly one form
 *  the engine can read; `custom` only ever appears for a value already stored that it cannot. */
type Mode = `relative` | `today` | `absolute` | `custom`

const MODE_CHOICES: Choice[] = [
  { value: `relative`, label: `Relative to today` },
  { value: `today`, label: `Today` },
  { value: `absolute`, label: `A specific date` },
]
/** Appended only while a stored value is unreadable, so nothing is silently rewritten under a user
 *  who saved it — the same rule the value dropdown follows for a renamed status. */
const CUSTOM_CHOICE: Choice = { value: `custom`, label: `Custom value` }

const DIRECTION_CHOICES: Choice[] = [
  { value: `before`, label: `before` },
  { value: `after`, label: `after` },
]
const UNIT_CHOICES: Choice[] = DATE_FILTER_UNITS.map((unit) => ({ value: unit.value, label: unit.label }))

/** The value editor for a condition on a DATE field.
 *
 *  A date rule is the one place in the builder where a free-text box is actively unsafe. The engine
 *  reads `$now-18y` and compares dates as text, so a value it cannot parse ("now-18y", a `$` short)
 *  sorts every date below it and matches everyone instead of nobody. Measured on dev 2026-08-28 and
 *  filed as TRK-5617. Offering the grammar as controls means the spelling is never typed at all.
 *
 *  Mode is DERIVED from the stored value wherever the value says what it is, so undo/redo and an
 *  externally replaced condition cannot leave the control describing something it is not. Local
 *  state only carries what an empty value cannot express: which mode a blank row is being written
 *  in, and the interval parts while the amount is still missing. */
export function DateValueField({
  value,
  onChange,
  invalid,
}: {
  value?: string
  onChange: (value: string | undefined) => void
  invalid?: boolean
}) {
  const parsed = parseDateFilterValue(value)
  const relative = parsed.kind === `relative` ? parsed : undefined

  const [blankMode, setBlankMode] = useState<Mode>(`relative`)
  const [draftAmount, setDraftAmount] = useState(``)
  const [draftUnit, setDraftUnit] = useState<DateFilterUnit>(`y`)
  const [draftDirection, setDraftDirection] = useState<DateFilterDirection>(`before`)

  const mode: Mode = parsed.kind === `unreadable` ? (parsed.value ? `custom` : blankMode) : parsed.kind
  const amount = relative ? String(relative.amount) : draftAmount
  const unit = relative ? relative.unit : draftUnit
  const direction = relative ? relative.direction : draftDirection

  const pushRelative = (nextAmount: string, nextUnit: DateFilterUnit, nextDirection: DateFilterDirection) => {
    setDraftAmount(nextAmount)
    setDraftUnit(nextUnit)
    setDraftDirection(nextDirection)
    setBlankMode(`relative`)
    const count = Number(nextAmount)
    onChange(
      nextAmount.trim() && Number.isInteger(count) && count > 0
        ? formatDateFilterValue({ kind: `relative`, direction: nextDirection, amount: count, unit: nextUnit })
        : undefined,
    )
  }

  const changeMode = (next: Mode) => {
    // Custom is only ever left, never entered. Re-selecting it must keep the stored value: clearing
    // it here would delete the very thing the mode exists to show back.
    if (next === `custom`) return
    setBlankMode(next)
    if (next === `today`) onChange(`$now`)
    else if (next === `relative`) pushRelative(draftAmount, draftUnit, draftDirection)
    // A specific date starts empty rather than guessing one.
    else onChange(undefined)
  }

  return (
    <div className="space-y-2">
      <ChoiceSelect
        value={mode}
        choices={mode === `custom` ? [...MODE_CHOICES, CUSTOM_CHOICE] : MODE_CHOICES}
        onChange={(v) => changeMode(v as Mode)}
      />
      {mode === `relative` && (
        // Reads as the sentence it becomes: "… less than · 18 · years · before · today".
        <div className="flex flex-wrap items-center gap-1.5">
          <TextField
            type="number"
            min={1}
            step={1}
            className="w-[4.5rem]"
            value={amount}
            placeholder="18"
            invalid={invalid && !amount}
            aria-label="Interval"
            onChange={(e) => pushRelative(e.target.value, unit, direction)}
          />
          <ChoiceSelect
            className="w-28"
            value={unit}
            choices={UNIT_CHOICES}
            onChange={(v) => pushRelative(amount, v as DateFilterUnit, direction)}
          />
          <ChoiceSelect
            className="w-24"
            value={direction}
            choices={DIRECTION_CHOICES}
            onChange={(v) => pushRelative(amount, unit, v as DateFilterDirection)}
          />
          <span className="text-xs text-subtle">today</span>
        </div>
      )}
      {mode === `absolute` && (
        <DateInput
          value={parsed.kind === `absolute` ? parsed.date : undefined}
          invalid={invalid}
          aria-label="Date"
          onChange={onChange}
        />
      )}
      {mode === `custom` && (
        <>
          <TextField
            value={value ?? ``}
            invalid
            aria-label="Custom value"
            onChange={(e) => onChange(e.target.value || undefined)}
          />
          <p className="text-[11px] leading-relaxed text-red-600">
            The engine cannot read this as a date. It would compare against the wrong day and match every contact that
            has this field, instead of none. Choose one of the options above.
          </p>
        </>
      )}
    </div>
  )
}
